import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatAbsoluteTime, severityVariant } from "@/lib/formatters";
import { isSearchInFlight } from "@/types/api";
import type { SearchResult } from "@/types/api";

function formatCell(col: string, value: unknown): string {
  if (value == null) return "";
  if (typeof value === "number") return value.toLocaleString();
  const str = String(value);
  // Match temporal columns explicitly: a bare `includes("at")` also matches
  // status_id and category_uid, rendering the integer 2 as a date (#905).
  if (/(^|[._])(time|timestamp|date|.*_at)($|[._])/.test(col)) {
    const d = new Date(str);
    if (!isNaN(d.getTime())) return d.toLocaleString();
  }
  return str;
}

function cellText(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
}

interface SearchResultsProps {
  result: SearchResult | null;
  isPolling: boolean;
  isAggregate: boolean;
  onEntityClick: (entityType: string, entityId: string) => void;
}

export function SearchResults({
  result,
  isPolling,
  isAggregate,
  onEntityClick,
}: SearchResultsProps) {
  const [visibleCount, setVisibleCount] = useState(200);
  const [showRaw, setShowRaw] = useState(false);
  if (isPolling || isSearchInFlight(result?.status)) {
    return (
      <div className="space-y-3 py-8 text-center">
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm font-medium">Running query...</span>
        </div>
        <Progress value={undefined} className="mx-auto w-64" />
        <p className="text-xs text-muted-foreground">
          Results will appear when the query completes
        </p>
      </div>
    );
  }

  if (!result) return null;

  if (result.status === "FAILED") {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
        Query failed: {result.error ?? "Unknown error"}
      </div>
    );
  }

  if (result.status === "CANCELLED") {
    return (
      <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 text-sm text-yellow-400">
        Query was cancelled.
      </div>
    );
  }

  const rows = result.results ?? [];

  if (rows.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
        No events found matching your query
      </div>
    );
  }

  if (isAggregate) {
    const columns = Object.keys(rows[0]);
    return (
      <div className="overflow-x-auto">
        <Table aria-label="Aggregate results">
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col} className="min-w-[120px] whitespace-nowrap">
                  {col}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                {columns.map((col) => (
                  <TableCell key={col} className="min-w-[120px] text-xs">
                    {formatCell(col, row[col])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  // Compute a basic summary from the rows
  const classCounts: Record<string, number> = {};
  const entities = new Map<string, Set<string>>();

  for (const row of rows) {
    const className = String(row.class_name ?? row.class_uid ?? "unknown");
    classCounts[className] = (classCounts[className] ?? 0) + 1;

    // Extract entities from common fields
    for (const [key, value] of Object.entries(row)) {
      if (
        typeof value === "string" &&
        (key.includes("ip") || key.includes("user") || key.includes("host"))
      ) {
        const entityType = key.includes("ip")
          ? "ip"
          : key.includes("user")
            ? "user"
            : "host";
        if (!entities.has(entityType)) {
          entities.set(entityType, new Set());
        }
        entities.get(entityType)!.add(value);
      }
    }
  }

  // Get table columns from first row
  const columns = Object.keys(rows[0]).slice(0, 6);

  return (
    <div className="space-y-4">
      {/* Results summary */}
      <div className="rounded-lg border bg-muted/30 p-4">
        <h3 className="mb-2 text-sm font-medium">
          Results Summary ({result.row_count ?? rows.length} events)
        </h3>
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(classCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
            .map(([cls, count]) => (
              <Badge key={cls} variant="secondary">
                {cls}: {count}
              </Badge>
            ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Events</h3>
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          aria-pressed={showRaw}
          onClick={() => setShowRaw((value) => !value)}
        >
          {showRaw ? "Show readable view" : "Show raw columns"}
        </Button>
      </div>

      {showRaw ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHead key={col} className="min-w-[120px] max-w-[300px] whitespace-nowrap">
                    {col}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.slice(0, visibleCount).map((row, idx) => (
                <TableRow key={idx}>
                  {columns.map((col) => (
                    <TableCell
                      key={col}
                      className="min-w-[120px] max-w-[300px] truncate text-xs"
                    >
                      {formatCell(col, row[col])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <ul className="divide-y rounded-lg border">
          {rows.slice(0, visibleCount).map((row, idx) => {
            const time = cellText(row.time);
            const severity = cellText(row.severity);
            const className = cellText(row.class_name) ?? cellText(row.class_uid);
            const activity = cellText(row.activity_name);
            const message = cellText(row.message);
            return (
              <li key={idx} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2 text-sm">
                <time dateTime={time ?? undefined} className="shrink-0 font-mono text-xs text-muted-foreground">
                  {time ? formatAbsoluteTime(time) : "—"}
                </time>
                <Badge variant={severityVariant((severity ?? "").toLowerCase())} className="text-xs">
                  {severity ?? "—"}
                </Badge>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {className ?? "—"}
                  {activity ? ` · ${activity}` : ""}
                </span>
                <span className="min-w-0 flex-1 break-words">{message ?? "—"}</span>
              </li>
            );
          })}
        </ul>
      )}
      {rows.length > visibleCount && (
        <div className="mt-2 flex items-center gap-2">
          <p className="text-xs text-muted-foreground">
            Showing {visibleCount} of {rows.length} results
          </p>
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setVisibleCount((c) => c + 200)}
          >
            Load 200 more
          </Button>
        </div>
      )}

      {/* Entities */}
      {entities.size > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-medium text-muted-foreground">
            Entities (click for timeline)
          </h3>
          <div className="flex flex-wrap gap-2">
            {Array.from(entities.entries()).map(([type, values]) =>
              Array.from(values)
                .slice(0, 10)
                .map((value) => (
                  <Badge
                    key={`${type}-${value}`}
                    variant="outline"
                    className="cursor-pointer font-mono text-xs hover:bg-accent"
                    onClick={() => onEntityClick(type, value)}
                  >
                    {value}
                  </Badge>
                )),
            )}
          </div>
        </div>
      )}
    </div>
  );
}
